import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PricingSection = () => {
  const navigate = useNavigate();
  const [selectedTier, setSelectedTier] = useState('professional');

  const pricingTiers = [
    {
      id: 'free',
      name: 'Free Plan',
      price: 0,
      description: 'Research and plan your book',
      icon: 'Gift',
      color: 'accent',
      features: [
        'Access to Agents 1-3',
        'Unlimited topic research',
        'Writing voice capture',
        'Unlimited outline creation',
        'Book project management'
      ],
      limitations: [
        'No AI manuscript writing',
        'No cover design generation',
        'No KDP publishing features',
        'No community access',
        'Upgrade required for PDF export'
      ],
      upgradeNote: 'Upgrade to any paid plan to start writing your book with AI'
    },
    {
      id: 'starter',
      name: 'Starter',
      price: 49,
      books: 1,
      description: 'Perfect for testing the platform',
      icon: 'BookOpen',
      color: 'primary',
      features: [
        'Create 1 complete book per month',
        'All 11 AI agents included',
        'Writing voice capture',
        'Print & ebook formatting',
        'Cover design generation',
        'KDP metadata creation',
        'Access to Authorpreneur Community',
        'Email support'
      ],
      limitations: [
        'Single book project',
        'Standard processing speed'
      ]
    },
    {
      id: 'professional',
      name: 'Professional',
      price: 99,
      books: 4,
      description: 'Most popular for serious authors',
      icon: 'Crown',
      color: 'secondary',
      popular: true,
      features: [
        'Create 4 complete books per month',
        'All 11 AI agents included',
        'Priority AI processing',
        'Advanced voice analysis',
        'Premium cover designs',
        'SEO-optimized metadata',
        'Access to Authorpreneur Community',
        'Priority email support',
        'Monthly strategy calls'
      ],
      limitations: []
    },
    {
      id: 'business',
      name: 'Business',
      price: 199,
      books: 10,
      description: 'For prolific content creators',
      icon: 'Briefcase',
      color: 'accent',
      features: [
        'Create 10 complete books per month',
        'All 11 AI agents included',
        'Fastest AI processing',
        'Multi-voice profiles',
        'Custom cover templates',
        'Advanced market research',
        'Access to Authorpreneur Community',
        'Priority support (24h)',
        'Weekly strategy calls',
        'Bulk export options'
      ],
      limitations: []
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      price: 999,
      books: 'Unlimited',
      description: 'For agencies and publishers',
      icon: 'Building',
      color: 'success',
      features: [
        'Unlimited book creation',
        'All 11 AI agents included',
        'Dedicated AI resources',
        'Team collaboration tools',
        'White-label options',
        'API access',
        'Access to Authorpreneur Community',
        'Dedicated account manager',
        'Custom integrations',
        'Training & onboarding'
      ],
      limitations: []
    }
  ];

  const handleSelectPlan = (tierId) => {
    if (tierId === 'free') {
      navigate('/user-registration?plan=free');
    } else {
      localStorage.setItem('selectedTier', tierId);
      navigate('/user-registration');
    }
  };

  return (
    <section className="pt-20 lg:pt-24 bg-gradient-to-br from-muted/30 to-background">
      <div className="container mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-accent/10 rounded-full mb-6">
            <Icon name="DollarSign" size={16} color="var(--color-accent)" />
            <span className="text-sm font-medium text-accent">Simple, Transparent Pricing</span>
          </div>

          <h2 className="text-3xl lg:text-5xl font-heading font-bold text-foreground mb-6">
            Choose Your Authority-Building Plan
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 mb-12">
          {pricingTiers?.map((tier) => (
            <div
              key={tier?.id}
              className={`relative bg-card border-2 rounded-2xl p-6 transition-all duration-300 ${
                tier?.popular
                  ? 'border-secondary shadow-xl scale-105'
                  : 'border-border hover:border-primary/50 hover:shadow-lg'
              }`}
            >
              {tier?.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-secondary text-secondary-foreground text-xs font-semibold rounded-full">
                  Most Popular
                </div>
              )}

              <div className="text-center mb-6">
                <div className={`w-16 h-16 mx-auto mb-4 rounded-xl bg-${tier?.color}/10 flex items-center justify-center`}>
                  <Icon name={tier?.icon} size={32} color={`var(--color-${tier?.color})`} />
                </div>

                <h3 className="text-2xl font-heading font-bold text-foreground mb-2">
                  {tier?.name}
                </h3>

                <p className="text-sm text-muted-foreground mb-4">
                  {tier?.description}
                </p>

                <div className="flex items-baseline justify-center space-x-1">
                  <span className="text-4xl font-bold text-foreground">
                    {tier?.price === 0 ? 'Free' : `$${tier?.price}`}
                  </span>
                  {tier?.price > 0 && (
                    <span className="text-muted-foreground">/month</span>
                  )}
                </div>

                {tier?.books && (
                  <div className="mt-2 flex items-center justify-center space-x-2">
                    <Icon name="BookOpen" size={16} color="var(--color-muted-foreground)" />
                    <span className="text-sm font-medium text-foreground">
                      {typeof tier?.books === 'number' ? `${tier?.books} books` : tier?.books}
                    </span>
                  </div>
                )}
              </div>

              <Button
                variant={tier?.popular ? 'default' : 'outline'}
                size="default"
                fullWidth
                iconName="ArrowRight"
                iconPosition="right"
                onClick={() => handleSelectPlan(tier?.id)}
                className="mb-6"
              >
                {tier?.id === 'free' ? 'Start Free' : 'Get Started'}
              </Button>

              <div className="space-y-3 mb-6">
                {tier?.features?.map((feature, index) => (
                  <div key={index} className="flex items-start space-x-2">
                    <Icon name="Check" size={16} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-foreground">{feature}</span>
                  </div>
                ))}
              </div>

              {tier?.limitations?.length > 0 && (
                <div className="pt-4 border-t border-border space-y-2">
                  {tier?.limitations?.map((limitation, index) => (
                    <div key={index} className="flex items-start space-x-2">
                      <Icon name="Minus" size={16} color="var(--color-muted-foreground)" className="flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-muted-foreground">{limitation}</span>
                    </div>
                  ))}
                </div>
              )}

              {tier?.upgradeNote && (
                <div className="mt-4 p-3 bg-accent/10 rounded-lg">
                  <p className="text-xs text-accent font-medium">{tier?.upgradeNote}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="text-center mb-12">
          <Button
            variant="outline"
            size="lg"
            iconName="GitCompare"
            iconPosition="left"
            onClick={() => navigate('/pricing-page')}
            className="hover:bg-primary/10 transition-colors"
          >
            Compare Plans
          </Button>
        </div>

        <div className="bg-card border border-border rounded-xl p-8 max-w-4xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="Users" size={24} color="var(--color-primary)" />
              </div>
              <div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-1">
                  Access to Authorpreneur Community
                </h3>
                <p className="text-sm text-muted-foreground">
                  All paid members get instant access to our exclusive community of authors, entrepreneurs, and publishing experts.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;